import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  LayoutDashboard, Inbox, GitBranch, RefreshCw, BarChart3, Wand2,
  Layers as LayersIcon, Share2, Brain, Settings as SettingsIcon,
  LogOut, Menu, X, Sparkles, Server, ChevronDown, UserCircle2, Plus, Check,
} from "lucide-react";

const LOGO = "/ashra-logo.png";

import { MODULE_STATUS } from "../lib/moduleStatus";

const NAV = [
  { to: "/dashboard",   label: "Command Center", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/capture",     label: "Capture",        icon: Inbox,           testid: "nav-capture",      sub: "Inbox · Leads · Opportunities" },
  { to: "/convert",     label: "Convert",        icon: GitBranch,       testid: "nav-convert",      sub: "Flows · Workforce · Strategist" },
  { to: "/recover",     label: "Recover",        icon: RefreshCw,       testid: "nav-recover",      sub: "Recovery · Lost revenue" },
  { to: "/insights",    label: "Insights",       icon: BarChart3,       testid: "nav-insights",     sub: "Analytics · Attribution · Forecast" },
  { to: "/forge",       label: "Forge",          icon: Wand2,           testid: "nav-forge",        sub: "Funnels · Content · Offers" },
  { to: "/experiences", label: "Experiences",    icon: LayersIcon,      testid: "nav-experiences",  sub: "Public Hub · Products · Memberships" },
  { to: "/amplify",     label: "Amplify",        icon: Share2,          testid: "nav-amplify",      sub: "Affiliates · Commissions" },
  { to: "/nexus",       label: "Nexus",          icon: Brain,           testid: "nav-nexus",        sub: "Memory · Voice · Knowledge" },
  { to: "/settings",    label: "Settings",       icon: SettingsIcon,    testid: "nav-settings",     sub: "Billing · Integrations · Account" },
].filter(n => MODULE_STATUS[n.to]?.status !== "hidden");

function WorkspaceSwitcher({ onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");

  const current = list.find(w => w.workspace_id === user?.workspace_id);

  useEffect(() => {
    api.get("/workspaces").then(r => setList(r.data || [])).catch(() => setList([]));
  }, [user?.workspace_id]);

  const create = async () => {
    if (!name || name.length < 2) return;
    try {
      await api.post("/workspaces", { name, handle: handle || undefined });
      setCreating(false); setName(""); setHandle("");
      const r = await api.get("/workspaces");
      setList(r.data || []);
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create workspace";
      try { (await import("sonner")).toast.error(msg); } catch { alert(msg); }
    }
  };

  const switchTo = async (ws_id) => {
    try {
      await api.post(`/workspaces/switch/${ws_id}`);
      onChanged?.();
      window.location.reload();
    } catch {}
  };

  return (
    <div className="relative mb-6">
      <button onClick={() => setOpen(!open)} data-testid="ws-switcher"
        className="w-full rounded-2xl border border-line bg-zinc-50 px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-100 transition focus-ring">
        <span className="h-7 w-7 rounded-lg ashra-gradient-bg grid place-items-center text-white shrink-0"><Sparkles className="h-3.5 w-3.5" /></span>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-[10px] uppercase tracking-wider text-meta font-bold leading-none">Workspace</p>
          <p className="text-sm font-bold text-ink leading-tight mt-0.5 truncate">{current?.name || "Workspace"}</p>
        </div>
        <ChevronDown className="h-4 w-4 text-meta" />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full rounded-2xl border border-line bg-white shadow-lg p-1.5" data-testid="ws-menu">
          <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-meta font-bold">Your workspaces</p>
          <div className="max-h-56 overflow-y-auto scrollbar-thin">
            {list.length === 0 && <p className="px-3 py-2 text-xs text-meta">No workspaces yet</p>}
            {list.map(w => {
              const isCurrent = w.workspace_id === user?.workspace_id;
              return (
                <button key={w.workspace_id} onClick={() => !isCurrent && switchTo(w.workspace_id)}
                  data-testid={`ws-item-${w.workspace_id}`}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm flex items-center gap-2 transition ${
                    isCurrent ? "bg-ai-soft text-ai font-semibold" : "text-ink-soft hover:bg-zinc-50"
                  }`}>
                  <span className="truncate flex-1">{w.name}</span>
                  {w.handle && <span className="text-[10px] text-meta font-mono">/{w.handle}</span>}
                  {isCurrent && <Check className="h-3.5 w-3.5 shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="border-t border-line mt-1.5 pt-1.5">
            {!creating ? (
              <button onClick={() => setCreating(true)} data-testid="ws-create-toggle"
                className="w-full px-3 py-2 rounded-xl text-sm flex items-center gap-2 text-ai font-semibold hover:bg-ai-soft transition">
                <Plus className="h-3.5 w-3.5" /> New workspace
              </button>
            ) : (
              <div className="p-2 space-y-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Workspace name"
                  data-testid="ws-create-name" className="w-full rounded-lg border border-line px-2.5 py-1.5 text-sm" />
                <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="public handle (optional)"
                  data-testid="ws-create-handle" className="w-full rounded-lg border border-line px-2.5 py-1.5 text-sm font-mono" />
                <div className="flex items-center gap-2">
                  <button onClick={create} data-testid="ws-create-submit"
                    className="rounded-full ashra-gradient-bg px-3 py-1.5 text-xs font-semibold text-white">Create</button>
                  <button onClick={() => setCreating(false)} className="text-xs text-meta">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-line px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard"><img src={LOGO} alt="Ashra AI" className="h-10 w-auto" /></Link>
        <button onClick={() => setOpen(!open)} className="p-2 rounded-lg border border-line min-h-[44px] min-w-[44px] grid place-items-center" data-testid="mobile-menu-toggle">
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-0 z-30 h-screen w-72 shrink-0 border-r border-line bg-white px-5 py-6 overflow-y-auto scrollbar-thin`} data-testid="sidebar">
          <Link to="/dashboard" className="flex items-center gap-2 mb-5">
            <img src={LOGO} alt="Ashra AI" className="h-11 w-auto" />
          </Link>

          <WorkspaceSwitcher />

          <nav className="space-y-1" data-testid="primary-nav">
            {NAV.map((n) => {
              const status = MODULE_STATUS[n.to]?.status || "operational";
              return (
                <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={() => setOpen(false)}
                  className={({ isActive }) => `flex items-start gap-3 rounded-xl px-3 py-2.5 transition group ${
                    isActive ? "ashra-gradient-bg text-white shadow-sm" : "text-ink-soft hover:bg-zinc-50"
                  }`}>
                  {({ isActive }) => (
                    <>
                      <n.icon className={`h-4 w-4 mt-0.5 shrink-0 ${isActive ? "text-white" : "text-meta group-hover:text-ai"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm font-semibold ${isActive ? "text-white" : "text-ink"}`}>{n.label}</p>
                          {status === "beta" && (
                            <span
                              className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-px ${
                                isActive ? "bg-white/25 text-white" : "bg-amber-100 text-amber-700"
                              }`}
                              data-testid={`badge-beta-${n.testid}`}
                            >
                              beta
                            </span>
                          )}
                        </div>
                        {n.sub && <p className={`text-[10px] mt-0.5 ${isActive ? "text-white/80" : "text-meta-soft"}`}>{n.sub}</p>}
                      </div>
                    </>
                  )}
                </NavLink>
              );
            })}
            {user?.is_admin && (
              <NavLink to="/admin" data-testid="nav-admin" onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                  isActive ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white" : "text-ink-soft hover:bg-zinc-50"
                }`}>
                <Server className="h-4 w-4" /> Super Admin
              </NavLink>
            )}
          </nav>

          <div className="mt-6 rounded-2xl border border-line bg-zinc-50 p-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full ashra-gradient-bg grid place-items-center text-white text-sm font-bold">{user?.name?.[0]?.toUpperCase() || "A"}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate text-ink">{user?.name}</p>
                <p className="text-[11px] text-meta truncate capitalize">{user?.plan} plan</p>
              </div>
              <button onClick={doLogout} className="p-2 rounded-lg hover:bg-zinc-100 min-h-[40px] min-w-[40px] grid place-items-center" data-testid="logout-btn"><LogOut className="h-4 w-4" /></button>
            </div>
            <Link to="/customer" className="mt-3 w-full inline-flex items-center justify-center gap-1 rounded-full border border-line bg-white px-3 py-2 text-xs font-medium text-ink-soft hover:bg-zinc-50 focus-ring" data-testid="open-customer-portal">
              <UserCircle2 className="h-3.5 w-3.5" /> Customer portal
            </Link>
          </div>
        </aside>

        <main className="flex-1 min-w-0 min-h-screen"><Outlet /></main>
      </div>
    </div>
  );
}
